import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { ChartData } from 'chart.js';
import { Observable, forkJoin, from, map } from 'rxjs';
import { User } from 'src/app/classes/models';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { WidgetPanelChart } from 'src/app/interfaces/widget-panel-chart';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { companyVendorsService } from 'src/app/shared/vive-api/company-vendors.service';
import { CompanyVendorsFindAllByCompanyIdRequestDtoRatingsEnum } from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

@Component({
  selector: 'app-vendor-ratings-widget',
  templateUrl: './vendor-ratings-widget.component.html',
  styleUrls: ['./vendor-ratings-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorRatingsWidgetComponent {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private Router: Router,
    private SharedDataService: SharedDataService,
    private ToastService: ToastService
  ) {}

  @Input() CompanyId!: number;
  userAccountRole?: UserAccountRoles;

  title: string = 'Vendor Ratings';
  widgetPanelChart: WidgetPanelChart[] = [];

  navigateToVendorRatingsPage() {
    this.Router.navigate(['client', this.CompanyId, 'vendor-ratings']);
  }

  private formatData(data: { key: string; value: number }[]) {
    const chartData: ChartData = {
      labels: data.map((data: { key: string }) => data.key),
      datasets: [
        {
          data: data.map((data: { value: number }) => data.value)
          // barPercentage: 0.4,
          // categoryPercentage: 0.9,
          // borderRadius: 5
        }
      ]
    };

    this.widgetPanelChart = [
      {
        title: this.title,
        data: {
          chartTitle: this.title,
          chartType: 'bar',
          chartData: chartData,
          chartOptions: {
            indexAxis: 'y',
            scales: {
              x: {
                display: false,
                border: {
                  display: false
                },
                grid: {
                  display: false
                }
              },
              y: {
                display: true,
                border: {
                  display: false
                },
                grid: {
                  display: false
                }
              }
            },
            layout: {
              padding: {
                right: 25
              }
            },
            plugins: {
              legend: {
                display: false
              }
            }
          },
          chartStyle: {},
          displayChartTitle: false
        }
      }
    ];
  }

  private navigateToMyVendorGrid(data: string) {
    const formattedData = data.match(/\d+/)?.[0];

    this.Router.navigateByUrl(`client/${this.CompanyId}/vendors/client`, {
      state: {
        FilterModel: {
          viveStatus: {
            filterType: 'set',
            values: [
              'Approved',
              'Suspended',
              'Registering',
              'Awaiting Payment',
              'Registered',
              'Reviewing',
              'Renewing',
              'Site Conditional',
              'Compliance Exempt',
              'Business Verified',
              'Non-Verified'
            ]
          },
          clientSpecificVendorRating: {
            filterType: 'set',
            values: [formattedData]
          }
        }
      }
    });
  }

  private loadVendorRatings() {
    // One counted page of one row per whole star of this client's floored
    // review average, so the API counts in SQL instead of sending every rated
    // relationship. Highest star first.
    const stars: CompanyVendorsFindAllByCompanyIdRequestDtoRatingsEnum[] = [
      '5',
      '4',
      '3',
      '2',
      '1',
      '0'
    ];
    const getStarCount = (
      star: CompanyVendorsFindAllByCompanyIdRequestDtoRatingsEnum
    ): Observable<{ key: string; value: number }> =>
      from(
        companyVendorsService.findAllByCompanyId({
          options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS },
          pathParams: { companyId: this.CompanyId },
          queryParams: { ratings: [star], pageSize: 1, includeCount: true }
        })
      ).pipe(
        map(({ meta }) => ({
          key: `${star} Star Rating`,
          value: meta.total ?? 0
        }))
      );

    forkJoin(stars.map(getStarCount)).subscribe({
      next: (starCounts) => {
        this.formatData(starCounts.filter(({ value }) => value > 0));
        this._cdr.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    this.SharedDataService.listenForEvent<
      string | null | undefined | User
    >().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'chartClicked' && typeof data === 'string') {
          const vendorRatings = Array.from({ length: 5 }).map(
            (value, index) => `${index + 1} Star Rating`
          );

          if (vendorRatings.includes(data)) {
            this.navigateToMyVendorGrid(data);
          }
        }

        if (eventName === 'SetLoggedInUser' && data instanceof User) {
          if (!data) {
            throw this.ToastService.addErrorToast(
              'Error',
              'There was an error retrieving user information.',
              { returnError: true }
            );
          }

          const { UserAccountRole } = data;

          if (!UserAccountRole) {
            throw this.ToastService.addErrorToast(
              'Error',
              'User is missing permission information.',
              { returnError: true }
            );
          }

          const { role } = UserAccountRole;

          if (!role) {
            throw this.ToastService.addErrorToast(
              'Error',
              'User is missing permission.',
              { returnError: true }
            );
          }

          // The dashboard answers every widget that asks for the user, so the
          // same user arrives once per asking widget. Load on the first.
          if (this.userAccountRole) {
            return;
          }

          this.userAccountRole = role;

          if (role === 'CLIENT_ADMIN') {
            this.loadVendorRatings();
          }
          this._cdr.markForCheck();
        }
      }
    });

    this.SharedDataService.sendEvent('GetLoggedInUser');
  }
}
